import React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./styles.module.css";
import { FaAngleDown, FaAngleUp } from "react-icons/fa6";
import { getLayerZIndex, getOverlayRoot } from "../../../utils/layerManager";
export function Dropdown({
  options = [],
  value,
  onChange,
  defaultValue,
  align = "left",
  className,
  placeholder = "Select an option",
  trigger,
  children,
  searchable = false,
  disabled = false,
  // SCRUM-787: opt-in escape hatch for a trigger that sits inside an
  // ancestor with non-visible overflow (e.g. the Billing table's row-actions
  // menu, clipped by the shared Table's horizontal-scroll wrapper whenever
  // there are too few rows for the menu to have room inside it) - renders
  // the menu into the shared overlay root instead of the normal DOM
  // position, positioned by viewport coordinates so no ancestor can clip
  // it. Left false by default since every other consumer relies on the
  // existing in-flow positioning and hasn't been checked against this path.
  usePortal = false,
}) {
  const [open, setOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [searchTerm, setSearchTerm] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const [portalPosition, setPortalPosition] = useState(null);
  const dropdownRef = useRef(null);
  const contentRef = useRef(null);
  const searchInputRef = useRef(null);

  // Portal mode gives the menu its natural width (min-width/content, e.g.
  // .rowActionsMenu's 240px) rather than matching the trigger's own box -
  // same as the in-flow width:100% version does once .rowActionsMenu's
  // min-width overrides it - so alignment has to be resolved against that
  // *measured* width, not the trigger's, or a right-aligned menu wider than
  // its trigger would hang off the trigger's left edge instead of lining up
  // its right edge with the trigger's right edge.
  const computePosition = () => {
    if (!dropdownRef.current || !contentRef.current) return;
    const triggerRect = dropdownRef.current.getBoundingClientRect();
    const contentHeight = contentRef.current.offsetHeight;
    const contentWidth = contentRef.current.offsetWidth;
    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;
    const shouldOpenUpward = spaceBelow < contentHeight && spaceAbove > spaceBelow;
    setOpenUpward(shouldOpenUpward);

    if (usePortal) {
      setPortalPosition({
        left: align === "right" ? triggerRect.right - contentWidth : triggerRect.left,
        top: shouldOpenUpward ? triggerRect.top - contentHeight - 8 : triggerRect.bottom + 8,
      });
    }
  };

  // SCRUM-787: the menu always opened downward with no regard for where the
  // trigger sits in the viewport - for a row near the bottom of a long
  // table (or just the bottom of the screen), that pushed part of the menu
  // off-screen, reading as "cut off". Measured after the content actually
  // renders (useLayoutEffect, before paint) rather than estimated, since
  // menu height varies per consumer/content.
  useLayoutEffect(() => {
    if (!open) return;
    computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, usePortal]);

  // Portal mode is fixed-positioned off the trigger's own viewport
  // coordinates, so a scroll/resize while open needs to recompute it -
  // in-flow mode doesn't need this since it just rides along with layout.
  useEffect(() => {
    if (!open || !usePortal) return;
    window.addEventListener("scroll", computePosition, true);
    window.addEventListener("resize", computePosition);
    return () => {
      window.removeEventListener("scroll", computePosition, true);
      window.removeEventListener("resize", computePosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, usePortal]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Portal mode renders the menu outside dropdownRef's own DOM subtree
      // (into the shared overlay root), so a click inside it would
      // otherwise register as "outside" and close the menu before its own
      // item's onClick ever fires - contentRef covers that regardless of
      // mode (a no-op check when not using a portal, since it's already
      // inside dropdownRef there).
      const clickedInside =
        dropdownRef.current?.contains(event.target) ||
        contentRef.current?.contains(event.target);
      if (!clickedInside) {
        setOpen(false);
        if (searchable) {
          setSearchTerm("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchable]);

  // Close dropdown when pressing escape
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleItemClick = (selectedValue, selectedLabel) => {
    if (onChange) {
      onChange(selectedValue);
    } else {
      setInternalValue(selectedValue);
    }
    setOpen(false);
    if (searchable) {
      setSearchTerm("");
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleTriggerClick = () => {
    if (!disabled) {
      setOpen(!open);
      if (searchable && !open) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }
  };

  const currentValue = value !== undefined ? value : internalValue;
  const selectedOption = options.find(
    (option) => option.value === currentValue
  );

  // Filter options based on search term
  const filteredOptions = searchable
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const renderTrigger = () => {
    if (trigger) {
      return React.cloneElement(trigger, {
        onClick: handleTriggerClick,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleTriggerClick();
          }
        },
        tabIndex: 0,
        role: "button",
        "aria-haspopup": "true",
        "aria-expanded": open,
        "data-selected-label": selectedOption
          ? selectedOption.label
          : placeholder,
      });
    }

    return (
      <div
        onClick={handleTriggerClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleTriggerClick();
          }
        }}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-haspopup="true"
        aria-expanded={open}
        className={`${styles.dropdownTrigger} ${disabled ? styles.disabled : ""}`}
      >
        <input
          className={styles.input}
          value={searchable && open ? searchTerm : (selectedOption ? selectedOption.label : placeholder)}
          placeholder={placeholder}
          disabled={disabled}
          onChange={searchable && open ? handleSearchChange : undefined}
          ref={searchable ? searchInputRef : undefined}
          readOnly={!searchable || !open}
        />

        <div className={styles.arrow}>
          {open ? <FaAngleUp /> : <FaAngleDown />}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (children) {
      return React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === DropdownItem) {
          return React.cloneElement(child, {
            onClick: () =>
              handleItemClick(
                child.props.value || child.props.children,
                child.props.children
              ),
            "aria-selected":
              child.props.value === currentValue ||
              child.props.children === currentValue,
          });
        }
        return child;
      });
    }

    return filteredOptions.map((option) => (
      <div
        key={option.value}
        onClick={() => handleItemClick(option.value, option.label)}
        className={`${styles.dropdownItem} ${
          option.value === currentValue ? styles.selected : ""
        }`}
        role="menuitem"
        tabIndex={-1}
        data-value={option.value}
        aria-selected={option.value === currentValue}
      >
        {option.label}
      </div>
    ));
  };

  const dropdownContentBody = (
    <>
      {searchable && (
        <div className={styles.searchContainer}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus
          />
        </div>
      )}
      {renderContent()}
      {searchable && filteredOptions.length === 0 && (
        <div className={styles.noResults}>No results found</div>
      )}
    </>
  );

  return (
    <div className={styles.dropdownContainer} ref={dropdownRef}>
      {renderTrigger()}

      {open && usePortal &&
        createPortal(
          <div
            ref={contentRef}
            className={`${styles.dropdownContent} ${className || ""}`}
            style={{
              position: "fixed",
              margin: 0,
              top: portalPosition ? `${portalPosition.top}px` : 0,
              left: portalPosition ? `${portalPosition.left}px` : 0,
              right: "auto",
              bottom: "auto",
              // Overrides .dropdownContent's width:100% (which, under
              // position:fixed, would resolve against the viewport instead
              // of the trigger) - "auto" instead lets the menu size itself
              // from its own content/min-width (e.g. .rowActionsMenu's
              // 240px), same as the in-flow version once that min-width
              // overrides its own width:100%.
              width: "auto",
              visibility: portalPosition ? "visible" : "hidden",
              zIndex: getLayerZIndex("dropdown"),
            }}
          >
            {dropdownContentBody}
          </div>,
          getOverlayRoot()
        )}

      {open && !usePortal && (
        <div
          ref={contentRef}
          className={`${styles.dropdownContent} ${
            align === "right" ? styles.alignRight : styles.alignLeft
          } ${openUpward ? styles.openUpward : ""} ${className || ""}`}
        >
          {dropdownContentBody}
        </div>
      )}
    </div>
  );
}

export function DropdownItem({
  children,
  onClick,
  disabled = false,
  className,
  value,
}) {
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={`${styles.dropdownItem} ${disabled ? styles.disabled : ""} ${
        className || ""
      }`}
      role="menuitem"
      tabIndex={-1}
      data-disabled={disabled || undefined}
      data-value={value}
    >
      {children}
    </div>
  );
}

export function DropdownSeparator({ className }) {
  return <div className={`${styles.dropdownSeparator} ${className || ""}`} />;
}

export function DropdownLabel({ children, className }) {
  return (
    <div className={`${styles.dropdownLabel} ${className || ""}`}>
      {children}
    </div>
  );
}
